import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n";

export const RentalSection = ({ id, titleKey, light, images, tabs }) => {
  const { t } = useLang();
  const [activeTab, setActiveTab] = useState(0);
  const [activeImg, setActiveImg] = useState(0);
  const activeImages = tabs ? tabs[activeTab].images : images;

  useEffect(() => {
    setActiveImg(0);
  }, [activeTab]);
  return (
    <section
      id={id}
      data-testid={`${id}-section`}
      className={`py-20 md:py-28 ${light ? "bg-sand text-ink" : "bg-coal text-sand"}`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-8"
        >
          <div className="max-w-xl">
            <p className="text-ember text-xs font-bold tracking-[0.35em] uppercase mb-4">{t("villas.kicker")}</p>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-none mb-4">{t(titleKey)}</h2>
            <p className={`text-sm md:text-base leading-relaxed ${light ? "text-ink/60" : "text-sand/60"}`}>{t("rental.sub")}</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col items-center gap-2">
              <a
                href="https://wa.me/38978321872"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${id}-whatsapp-button`}
                className="inline-flex items-center gap-3 px-7 py-3.5 text-xs font-bold tracking-[0.2em] uppercase transition-transform duration-300 hover:scale-[1.03]"
                style={{ backgroundColor: "#25D366", color: "#0b1f13" }}
              >
                <img src="https://cdn.simpleicons.org/whatsapp/0b1f13" alt="WhatsApp" className="w-5 h-5" /> WhatsApp
              </a>
            </div>
            <div className="flex flex-col items-center gap-2">
              <a
                href="viber://chat?number=%2B38978321872"
                data-testid={`${id}-viber-button`}
                className="inline-flex items-center gap-3 px-7 py-3.5 text-xs font-bold tracking-[0.2em] uppercase text-white transition-transform duration-300 hover:scale-[1.03]"
                style={{ backgroundColor: "#7360F2" }}
              >
                <img src="https://cdn.simpleicons.org/viber/ffffff" alt="Viber" className="w-5 h-5" /> Viber
              </a>
            </div>
          </div>
        </motion.div>
        {tabs && (
          <div className="flex flex-wrap gap-3 mt-10" data-testid={`${id}-tabs`}>
            {tabs.map((tab, i) => (
              <button
                key={tab.label || tab.labelKey}
                data-testid={`${id}-tab-${i}`}
                onClick={() => setActiveTab(i)}
                className={`px-6 py-2.5 text-xs font-bold tracking-[0.2em] uppercase border transition-colors duration-300 ${
                  i === activeTab
                    ? "bg-ember text-coal border-ember"
                    : light
                      ? "border-ink/25 text-ink/70 hover:border-ember hover:text-ember"
                      : "border-sand/30 text-sand/80 hover:border-ember hover:text-ember"
                }`}
              >
                {tab.labelKey ? t(tab.labelKey) : tab.label}
              </button>
            ))}
          </div>
        )}
        {tabs && (!activeImages || activeImages.length === 0) && (
          <div key={`empty-${activeTab}`} className="mt-12 md:mt-16 border border-sand/15 bg-coal text-sand p-12 md:p-16 text-center" data-testid={`${id}-tab-empty`}>
            <p className="font-serif italic text-4xl md:text-5xl text-ember mb-6" data-testid={`${id}-tab-empty-title`}>{tabs[activeTab].label || t(tabs[activeTab].labelKey)}</p>
            <p className="text-sand/60 text-sm md:text-base max-w-md mx-auto leading-relaxed">{t("rental.sub")}</p>
          </div>
        )}
        {activeImages && activeImages.length > 0 && (
          <div key={tabs ? activeTab : "single"} className="mt-12 md:mt-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.985 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
              className={`spotlight-frame aspect-[16/10] md:aspect-[16/8] mb-3 border ${light ? "border-ink/10" : "border-sand/10"}`}
            >
              <img src={activeImages[activeImg]} alt={t(titleKey)} data-testid={`${id}-main-image`} className="w-full h-full object-cover" />
            </motion.div>
            <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: "thin" }} data-testid={`${id}-thumbs`}>
              {activeImages.map((img, i) => (
                <button
                  key={img}
                  data-testid={`${id}-thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`spotlight-frame shrink-0 w-32 md:w-44 aspect-[16/10] border transition-colors duration-300 ${
                    activeImg === i
                      ? light
                        ? "border-terra"
                        : "border-ember"
                      : light
                        ? "border-ink/15 opacity-70 hover:opacity-100"
                        : "border-sand/20 opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={img} alt={`${t(titleKey)} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
